import { useTranslation } from 'react-i18next';
import { PanelLeft, X } from 'lucide-react';
import type { PageRecord } from '@nexus/store';
import { useUi, viewKey } from '../state/ui';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { cn } from '../lib/cn';
import { ModuleIcon } from './ModuleIcon';
import { useIsMobile } from '../lib/mobile';
import { usePlugins } from '../plugins/runtime';
import { PageIcon } from './PageIcon';

export function Tabs({ byId }: { byId: Map<string, PageRecord> }) {
  const { t } = useTranslation();
  const { tabs, active, focusTab, closeTab, sidebarOpen, drawerOpen, set } = useUi();
  const mobile = useIsMobile();
  const plugins = usePlugins();

  return (
    <div className="flex h-12 shrink-0 items-center gap-1 px-2">
      <button
        type="button"
        aria-label={t('shortcuts.toggleSidebar')}
        aria-pressed={mobile ? drawerOpen : sidebarOpen}
        onClick={() =>
          mobile ? set({ drawerOpen: !drawerOpen }) : set({ sidebarOpen: !sidebarOpen })
        }
        className="grid size-9 place-items-center md:size-7 rounded-md text-fg-muted hover:bg-bg-hover"
      >
        <PanelLeft className="size-4" />
      </button>
      <nav
        aria-label={t('tabs.label')}
        className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
      >
        {tabs.map((tab, i) => {
          const label =
            tab.kind === 'trash'
              ? t('trash.title')
              : tab.kind === 'tag'
                ? `#${tab.tag}`
                : tab.kind === 'search'
                  ? t('search.title')
                  : tab.kind === 'import'
                    ? t('import.title')
                    : tab.kind === 'nexus-import'
                      ? t('nexusImport.title')
                      : tab.kind === 'tasks'
                        ? t('tasks.title')
                        : tab.kind === 'projects'
                          ? t('projects.title')
                          : tab.kind === 'dashboard'
                            ? t('dashboard.label')
                            : tab.kind === 'graph'
                              ? t('graph.title')
                              : tab.kind === 'templates'
                                ? t('templates.title')
                                : tab.kind === 'memos'
                                  ? t('memos.title')
                                  : tab.kind === 'tips'
                                    ? t('tips.title')
                                    : tab.kind === 'plugins'
                                      ? t('plugins.title')
                                      : tab.kind === 'plugin'
                                        ? (plugins
                                            ?.find((p) => p.pageId === tab.pluginId)
                                            ?.manifest.views.find((v) => v.id === tab.viewId)
                                            ?.title ?? t('plugins.title'))
                                        : byId.get(tab.pageId)?.title || t('page.untitled');
          const selected = i === active;
          return (
            <div
              key={viewKey(tab)}
              className={cn(
                'group flex h-8 max-w-52 min-w-0 items-center rounded-full text-sm transition-colors',
                selected
                  ? 'bg-accent-soft font-medium text-fg'
                  : mobile
                    ? 'text-fg'
                    : 'text-fg-muted hover:bg-bg-hover hover:text-fg',
              )}
            >
              <button
                type="button"
                aria-current={selected ? 'page' : undefined}
                onClick={() => focusTab(i)}
                onAuxClick={(e) => e.button === 1 && closeTab(i)}
                className={cn(
                  'flex min-w-0 items-center gap-1.5 pl-2',
                  !selected && mobile ? 'pr-2' : 'pr-1',
                )}
              >
                {tab.kind === 'page' && byId.get(tab.pageId)?.icon ? (
                  <span aria-hidden className="text-sm leading-none">
                    <PageIcon icon={byId.get(tab.pageId)!.icon} />
                  </span>
                ) : (
                  <ModuleIcon kind={tab.kind} bare />
                )}
                <span className="truncate">{label}</span>
              </button>
              <button
                type="button"
                aria-label={`${t('tabs.close')}: ${label}`}
                onClick={() => closeTab(i)}
                className={cn(
                  'mr-1 grid size-5 shrink-0 place-items-center rounded-full hover:bg-bg-active',
                  // Touch screens have no hover: only the open tab shows its close button.
                  !selected &&
                    (mobile ? 'hidden' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'),
                )}
              >
                <X className="size-3" />
              </button>
            </div>
          );
        })}
      </nav>
      <NotificationCenter />
    </div>
  );
}
